'use client';

import { Canvas as OffscreenCanvas } from '@react-three/offscreen';
import { usePathname } from 'next/navigation';
import type { FC, ReactNode } from 'react';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FlightHud,
  SkySceneContent,
  updateSky,
  cameraRigForPath,
  useSkyData,
  useSkyWorker,
  useCloudPointer,
  INITIAL_SCROLL_FRAME,
  SkyShellContext,
  type CameraFrame,
  type SkyState,
  type SkyScrollFrame,
  type SkyScrollListener,
} from '@/features/sky';
import { DataDebug } from '@/features/diagnostics';
import styles from './layout.module.css';

const OUTRO_VH = 1;

const clamp01 = (value: number) => Math.min(1, Math.max(0, value));

// The canvas bridge stays mounted across scroll-driven layout renders.
// Worker state and stable fallback callbacks keep those renders out of the scene.
const SkyCanvas: FC<{
  worker: Worker;
  onFallbackFps: (fps: number) => void;
  onFallbackCamera: (camera: CameraFrame) => void;
  onFallbackReady: () => void;
}> = memo(({ worker, onFallbackFps, onFallbackCamera, onFallbackReady }) => (
  <OffscreenCanvas
    worker={worker}
    fallback={
      <SkySceneContent
        onFps={onFallbackFps}
        onCamera={onFallbackCamera}
        onReady={onFallbackReady}
      />
    }
    camera={{ position: [0, 1.8, 0], fov: 55, near: 1, far: 4e5 }}
    gl={{ depth: false, toneMappingExposure: 10 }}
    // 1:1 with CSS pixels regardless of display density — the scanlines must
    // be a true 2px, as on a non-retina monitor.
    dpr={1}
  />
));
SkyCanvas.displayName = 'SkyCanvas';

export default function SiteLayout({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  const rig = cameraRigForPath(pathname);
  const {
    worker,
    workerRef,
    badgeRef,
    writeFps,
    writeCamera,
    markReady,
    ready,
    intro,
    cameraFrameRef,
    subscribeCamera,
  } = useSkyWorker({ rig });
  useCloudPointer(workerRef);
  const [trafficInteractive, setTrafficInteractive] = useState(false);

  const { flights } = useSkyData(worker, workerRef);
  const showFps =
    process.env.NODE_ENV === 'development' ||
    (pathname?.startsWith('/sky') ?? false);

  const [skyVisible, setSkyVisible] = useState(false);
  useEffect(() => {
    if (!ready) return;
    const id = requestAnimationFrame(() => setSkyVisible(true));
    return () => cancelAnimationFrame(id);
  }, [ready]);

  const setSkyState = useCallback(
    (partial: Partial<SkyState>) => {
      updateSky(partial);
      workerRef.current?.postMessage({ type: 'sky-state', state: partial });
    },
    [workerRef]
  );

  useEffect(() => {
    setSkyState({ cloudsClear: false, yaw: 0, hoveredFlight: null });
  }, [rig, setSkyState, worker]);

  const frameRef = useRef<SkyScrollFrame>(INITIAL_SCROLL_FRAME);
  const listenersRef = useRef(new Set<SkyScrollListener>());
  const subscribeScroll = useCallback((listener: SkyScrollListener) => {
    listenersRef.current.add(listener);
    listener(frameRef.current);
    return () => {
      listenersRef.current.delete(listener);
    };
  }, []);

  useEffect(() => {
    const scroller = document.scrollingElement ?? document.documentElement;
    let raf = 0;
    const compute = () => {
      raf = 0;
      const h = window.innerHeight || 1;
      const y = scroller.scrollTop;
      const total = Math.max(1, scroller.scrollHeight - h);
      const progress = clamp01(y / total);
      const rampIn = clamp01(y / h);
      const rampOut = clamp01((total - y) / (h * OUTRO_VH));
      const reveal = clamp01((0.75 - rampOut) / 0.55);

      setTrafficInteractive(reveal > 0.4);
      setSkyState({
        scroll: progress,
        darken: Math.min(rampIn, rampOut),
        trafficReveal: reveal,
      });

      frameRef.current = { rampIn, rampOut, reveal, progress };
      for (const listener of listenersRef.current) listener(frameRef.current);
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(compute);
    };

    scroller.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    compute();
    return () => {
      scroller.removeEventListener('scroll', onScroll);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
    // pathname: the arriving page has its own length, so the ramps have to be
    // re-derived against it.
  }, [setSkyState, pathname]);

  const value = useMemo(
    () => ({
      ready,
      cameraFrameRef,
      subscribeCamera,
      setSkyState,
      subscribeScroll,
    }),
    [ready, cameraFrameRef, subscribeCamera, setSkyState, subscribeScroll]
  );

  return (
    <SkyShellContext.Provider value={value}>
      <div className={styles.container}>
        <div className={styles.fixedScene}>
          <div
            className={styles.sceneReveal}
            style={{
              opacity: skyVisible ? 1 : 0,
              transition: `opacity ${intro.fadeMs}ms ease-out`,
            }}
          >
            {worker && (
              <SkyCanvas
                worker={worker}
                onFallbackFps={writeFps}
                onFallbackCamera={writeCamera}
                onFallbackReady={markReady}
              />
            )}
          </div>
        </div>
      </div>

      <div className={styles.content}>{children}</div>

      {ready && rig.traffic && (
        <FlightHud
          cameraRef={cameraFrameRef}
          flights={flights}
          interactive={trafficInteractive}
          onHover={(id) => setSkyState({ hoveredFlight: id })}
        />
      )}

      {showFps && <div className={styles.fpsBadge} ref={badgeRef} />}
      {process.env.NODE_ENV === 'development' && <DataDebug />}
    </SkyShellContext.Provider>
  );
}
